// RTable: one member table of an RTableGroup, as a proper nested RObject
// living on a scoped projection of the group's DAG (the nested-RSet-element
// pattern). Tables exist by schema — there is no creation op and no factory:
// tableId = deriveTableId(groupId, tableName), and tables are only
// constructed through their group (group.getTable), never via the registry.
//
// ACTIONS (see payload.ts for formats; ops never carry the table name — the
// group-level envelope tags it):
//
//   insert
//     Creates a row. Carries: `rowId` (must equal deriveRowId(uuid, author)),
//     `uuid`, column `values`, optional author/signature. Values of
//     `pub` columns are exported to entry meta (derived, re-checked at
//     validation).
//
//   update
//     Partial row update: `rowId` + only the changed `values` (per-field
//     last-writer-wins, deterministic entry-hash tiebreak for concurrent
//     writers). The row must be live at the op's position. `readonly`
//     columns are fixed at insert and cannot be updated; `pub` columns CAN
//     be updated (independent modifiers) and export their new values to
//     entry meta. Optional authoring. Empty `values` rejected.
//
//   delete
//     Deletes a row by `rowId`. PERMANENT: rowIds are write-once identities,
//     so a deleted rowId can never be re-inserted ("restoring" means
//     inserting a new row with a fresh uuid). Always barrier-tagged; the
//     concurrentDeletes flag (default true) is resolved at-use at the delete's
//     own position (observed from the view `from`) and governs whether that
//     barrier hides the row at concurrent view positions. Optional authoring.
//
// Preconditions (see ../rschema/payload.ts) are derived from the schema
// only — FKs (column -> table, AT-USE) and restrictions (at-use predicates
// tagged insert / update / delete / all). Never serialized in row op payloads.
//
// Provides insert + update + delete writers, permanent-delete liveness
// (causal + at-use concurrentDeletes barriers), per-field LWW value resolution,
// pub meta export (inserts and updates), views, plus the enforcement layers in
// view.ts. Validation hard-rejects row ops whose restriction predicate fails at
// the parent frontier `(at, at)`. Entries are still rechecked at-use in views:
// a concurrent barrier delete of a witness / FK target voids the use, while a
// causally-later one does not. Voided ops are invisible (a voided insert never
// lives; a voided FK-update reverts via LWW); FK reference cycles resolve to
// DENY. Cross-group FK / exists targets resolve through bound foreign groups.
// When the group selects an identity provider, an authored op's signature is
// verified AT VALIDATION (hard reject / defer), so view-time evaluation trusts
// op.author. Write-time validation rejects dangling FKs (local and cross-group);
// the subject-row liveness check for update/delete is BASE delete-state only
// (an undeleted row stays repairable). Delta is the group's (a table never leads
// one): a member table contributes a per-table row-change accumulator
// (createDeltaAccumulator -> ./delta.ts) that the group routes walked entries
// to; computeDelta throws here.
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { formatValidationFailure, ValidationRejectedError, } from "@hyper-hyper-space/hhs3_mvt";
import { signPayload as signPayloadHelper } from "@hyper-hyper-space/hhs3_mvt";
import { deriveRowOpInnerMeta } from "../rtable_group/scopes.js";
import { deriveRowId } from "./hash.js";
import { RTableViewImpl } from "./view.js";
import { RTableDeltaAccumulator } from "./delta.js";
export const RTABLE_TYPE_ID = 'hhs/rtable_v1';
export class RTableImpl {
    constructor(tableId, tableName, group) {
        this.tableId = tableId;
        this.tableName = tableName;
        this.group = group;
    }
    getId() { return this.tableId; }
    getType() { return RTableImpl.typeId; }
    getBackendLabel() { return this.group.getBackendLabel(); }
    getTableName() { return this.tableName; }
    getGroupId() { return this.group.getId(); }
    // The group resolves the effective schema (pinned version ∪ ref-advances);
    // versions are identity across nesting, so the table's positions are
    // valid group positions.
    resolveSchemaView(at, from) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.group.resolveSchemaView(at, from);
        });
    }
    // A cross-group FK / exists target: the group resolves it through the
    // bound foreign group at the observed foreign version (see group.ts).
    resolveForeignTableView(group, table, at, from) {
        return this.group.resolveForeignTableView(group, table, at, from);
    }
    // Entry voiding is the group's computation (an entry may carry ops for
    // several tables and voids as a unit); see isEntryVoided in group.ts.
    isEntryVoided(entryHash, from) {
        return this.group.isEntryVoided(entryHash, from);
    }
    explainEntryVoided(entryHash, from) {
        return this.group.explainEntryVoided(entryHash, from);
    }
    // Write-time identity check: base delete-state liveness (no FK reach, no
    // view-time restriction recheck — see view.ts). An undeleted but FK-hidden
    // row is still a valid update target (it can be repaired).
    baseHasRow(rowId, at) {
        return __awaiter(this, void 0, void 0, function* () {
            return new RTableViewImpl(this, at, at).hasRowBase(rowId);
        });
    }
    // Row writers. `at` defaults to the GROUP frontier: by default a write
    // extends the group's consistent snapshot (not just this table's scope).
    insert(uuid, values, author, at) {
        return __awaiter(this, void 0, void 0, function* () {
            at = at !== null && at !== void 0 ? at : yield (yield this.group.getScopedDag()).getFrontier();
            const base = {
                action: 'insert',
                rowId: deriveRowId(uuid, author === null || author === void 0 ? void 0 : author.keyId),
                uuid,
                values,
            };
            const payload = author !== undefined
                ? yield signPayloadHelper(base, author)
                : base;
            return this.appendRowOp(payload, at);
        });
    }
    update(rowId, values, author, at) {
        return __awaiter(this, void 0, void 0, function* () {
            at = at !== null && at !== void 0 ? at : yield (yield this.group.getScopedDag()).getFrontier();
            const base = { action: 'update', rowId, values };
            const payload = author !== undefined
                ? yield signPayloadHelper(base, author)
                : base;
            return this.appendRowOp(payload, at);
        });
    }
    delete(rowId, author, at) {
        return __awaiter(this, void 0, void 0, function* () {
            at = at !== null && at !== void 0 ? at : yield (yield this.group.getScopedDag()).getFrontier();
            const base = { action: 'delete', rowId };
            const payload = author !== undefined
                ? yield signPayloadHelper(base, author)
                : base;
            return this.appendRowOp(payload, at);
        });
    }
    // Append one row op through the table's scoped DAG: the TableScope wraps
    // it into a row envelope, wraps the inner meta (t-<table>- prefix, outer
    // `tables` tag, hoisted barrier) and re-checks meta + payload validity.
    appendRowOp(op, at) {
        return __awaiter(this, void 0, void 0, function* () {
            const schemaView = yield this.group.resolveSchemaView(at);
            if (!schemaView.hasTable(this.tableName)) {
                throw new Error(`Table '${this.tableName}' does not exist in the effective schema at this position`);
            }
            const result = yield this.validatePayload(op, at);
            if (!result.valid) {
                throw new ValidationRejectedError(formatValidationFailure(result.why), result.why);
            }
            const innerMeta = deriveRowOpInnerMeta(op, this.tableName, schemaView);
            const scopedDag = yield this.getScopedDag();
            return scopedDag.append(op, innerMeta, at);
        });
    }
    // RObject interface
    validatePayload(payload, at) {
        return __awaiter(this, void 0, void 0, function* () {
            const envelope = { action: 'row', table: this.tableName, op: payload };
            return this.group.validatePayload(envelope, at);
        });
    }
    applyPayload(payload, at) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.appendRowOp(payload, at);
        });
    }
    // Default horizon is the GROUP frontier, not this table's scoped frontier.
    // The table scope's frontier projects the group tip down through
    // baseFilter (tables=this table), which drops trailing ref-advance
    // barriers (schema deploys, foreign observe ops). Those barriers are
    // exactly what at-use voiding must see: a concurrent cap revoke observed
    // via UPDATE REF lives in a barrier entry above this table's last row
    // write. Anchoring at the group frontier keeps those barriers in scope
    // (the extra non-barrier other-table entries it also drags in are inert
    // under this table's cover queries), and matches what RTableGroup.getView
    // / RTableGroupView.getTableView already pass down.
    getView(at, from) {
        return __awaiter(this, void 0, void 0, function* () {
            const groupDag = yield this.group.getScopedDag();
            const frontier = yield groupDag.getFrontier();
            at = at !== null && at !== void 0 ? at : frontier;
            from = from !== null && from !== void 0 ? from : frontier;
            return new RTableViewImpl(this, at, from);
        });
    }
    // A nested table never leads a delta (the group orchestrates bounds + walk);
    // it participates only via createDeltaAccumulator, like a nested RSet.
    computeDelta(_start, _end) {
        return __awaiter(this, void 0, void 0, function* () {
            throw new Error("computeDelta is not supported on a nested RTable (the group leads the delta)");
        });
    }
    createDeltaAccumulator(start, end) {
        return new RTableDeltaAccumulator(this, start, end);
    }
    extractForeignDeps(_payload, _at) {
        return undefined; // foreign deps are the group's concern
    }
    subscribe(_callback) {
        throw new Error("Method not implemented.");
    }
    unsubscribe(_callback) {
        throw new Error("Method not implemented.");
    }
    getScopedDag() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this._scopedDag === undefined) {
                this._scopedDag = yield this.group.getScopedDagForChild(this.tableId);
            }
            return this._scopedDag;
        });
    }
    getCausalDag() {
        return __awaiter(this, void 0, void 0, function* () {
            return this.group.getCausalDag();
        });
    }
    destroy() {
        return __awaiter(this, void 0, void 0, function* () {
            this._scopedDag = undefined;
        });
    }
}
RTableImpl.typeId = RTABLE_TYPE_ID;
export { RTableViewImpl } from "./view.js";
